/**
 * Month labels for the reporting period AFTER a given ISO date (`yyyy-MM-dd`):
 * the gap-window explainer's "{NextMonth} activity is tracked separately" line
 * and the ledger's collapsed next-period bucket header.
 *
 * Derived from the day AFTER `afterDate` (period end + 1 day), NOT
 * `setMonth(getMonth() + 1)`: adding a month to a 31-day period end (e.g. Jan 31)
 * overflows the shorter next month and rolls forward two months (Jan 31 -> Mar 3,
 * so "March"). The day after a period end is the first day of the next period,
 * which never overflows. Noon avoids DST edge cases; this formats a KNOWN date,
 * so it is not the campaign-"today" golden rule (which bans `new Date()` for
 * deriving "today").
 */
function firstDayAfter(afterDate: string): Date {
  const d = new Date(afterDate + 'T12:00:00');
  d.setDate(d.getDate() + 1);
  return d;
}

/** Month name only, e.g. "July". */
export function nextMonthName(afterDate: string): string {
  return firstDayAfter(afterDate).toLocaleDateString('en-US', { month: 'long' });
}

/** Full "Month YYYY" label, e.g. "July 2026". */
export function nextMonthLabel(afterDate: string): string {
  return firstDayAfter(afterDate).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Full "Month YYYY" label for the month a reporting period falls in, e.g.
 * "March 2026". Anchored on the period's OWN start date (unlike nextMonthLabel,
 * which anchors on the day AFTER a period end). Noon avoids DST edges; this
 * formats a KNOWN ISO date, so it is NOT the campaign-"today" golden rule
 * (which bans new Date() for deriving "today").
 */
export function monthYearLabel(iso: string): string {
  return new Date(iso + 'T12:00:00').toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
}

/**
 * Cadence-agnostic label for a reporting period `[start, end]` (both inclusive
 * ISO `yyyy-MM-dd`). The close surface files periods of every shape — AL monthly,
 * FEC quarterly, and the AL pre-election weekly/daily windows — so the label
 * must read from the actual bounds, NOT the start month alone (which mislabels a
 * quarter as its first month, e.g. "April" for Apr–Jun, and a week as its whole
 * month). Noon anchors avoid DST edges; these are KNOWN dates, so this is not the
 * campaign-"today" golden rule (which bans new Date() for deriving "today").
 *
 *   Full calendar month      → "June 2026"        (monthly)
 *   Multi-month, same year    → "Apr–Jun 2026"     (quarterly / semi-annual)
 *   Multi-month, cross-year   → "Nov 2026–Jan 2027"
 *   Partial month, day range  → "Oct 15–21, 2026"  (weekly pre-election)
 *   Single day                → "Oct 20, 2026"     (daily pre-election)
 */
export function periodLabel(start: string, end: string): string {
  const s = new Date(start + 'T12:00:00');
  const e = new Date(end + 'T12:00:00');
  const sameMonth =
    s.getFullYear() === e.getFullYear() && s.getMonth() === e.getMonth();

  if (sameMonth) {
    const lastDay = new Date(e.getFullYear(), e.getMonth() + 1, 0).getDate();
    // A full calendar month reads as the month (the monthly cadence).
    if (s.getDate() === 1 && e.getDate() === lastDay) {
      return s.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    // A partial window within one month reads as a day (range) — weekly/daily.
    const mon = s.toLocaleDateString('en-US', { month: 'short' });
    return s.getDate() === e.getDate()
      ? `${mon} ${s.getDate()}, ${e.getFullYear()}`
      : `${mon} ${s.getDate()}–${e.getDate()}, ${e.getFullYear()}`; // en-dash
  }

  // Spans multiple months → a month range (quarterly / semi-annual).
  const sMon = s.toLocaleDateString('en-US', { month: 'short' });
  const eMon = e.toLocaleDateString('en-US', { month: 'short' });
  if (s.getFullYear() === e.getFullYear()) {
    return `${sMon}–${eMon} ${e.getFullYear()}`; // en-dash
  }
  return `${sMon} ${s.getFullYear()}–${eMon} ${e.getFullYear()}`; // en-dash
}
